import { globalTheme } from '@frontend/components/external-providers';
import { TextareaTooltip } from '@frontend/components/ui';
import { Box, TextField } from '@mui/material';
import { styled } from '@mui/system';
import { get, isNull } from 'lodash';
import {
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface EditorLabelProps {
  defaultValue?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  color?: string;
  error?: boolean | string;
  size?: 'subtitle1' | 'subtitle2' | 'h6' | 'body2';
  maxLength?: number;
  onBlur?: (val: string) => void;
  isShowTooltips?: boolean;
  onFocusLabel?: (isFocus: boolean) => void;
  placeholder?: string;
  helperText?: string;
  inputRef?: MutableRefObject<null>;
}

// 點一下進編輯的位移容許值(px)：超過視為拖曳節點
const EDIT_TAP_SLOP = 4;

const EditorLabelContainerStyled = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

// WHY $editing：名稱輸入框佔滿節點標題列，若輸入框永遠接收滑鼠事件（且掛 nodrag），標題列就只剩
// 左側小圖示能拖動節點（追蹤解鎖內容模板客訴「節點拖不動」）。未編輯時讓輸入框不吃滑鼠事件，
// 按住拖曳＝搬節點（mousedown 落在容器上，reactflow 正常接手）、點一下＝由容器進入編輯並 focus；
// 編輯中才掛 nodrag，滑鼠選字才不會被 reactflow 當成拖曳。
const TextFieldStyled = styled(TextField, {
  shouldForwardProp: (prop) => prop !== '$editing',
})<{ $editing: boolean }>(({ theme, disabled, $editing }) => ({
  width: '100%',
  ...(!$editing && { pointerEvents: 'none' }),
  '& fieldset': {
    borderColor: `transparent ${disabled ? '!important' : ''}`,
  },
}));

export const EditorLabel = ({
  color,
  defaultValue,
  error,
  size = 'subtitle1',
  maxLength = 20,
  disabled = false,
  fullWidth = false,
  onBlur,
  isShowTooltips = false,
  onFocusLabel,
  placeholder,
  helperText = '',
  inputRef,
}: EditorLabelProps) => {
  const [value, setValue] = useState<string>(defaultValue ? defaultValue : '');
  const [isFocusLabel, setIsFocusLabel] = useState<boolean>(false);
  const innerInputRef = useRef<HTMLInputElement | null>(null);

  // 同時餵給外部傳入的 inputRef 與內部 ref，容器點擊時才能自行 focus 輸入框
  const setInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      innerInputRef.current = el;
      if (inputRef && !isNull(inputRef)) {
        (inputRef as MutableRefObject<HTMLInputElement | null>).current = el;
      }
    },
    [inputRef],
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur(value);
    }
    setIsFocusLabel(false);
  }, [onBlur, value]);

  const handleFocusLabel = useCallback(() => {
    if (onFocusLabel) {
      onFocusLabel(true);
    }
    setIsFocusLabel(true);
  }, [onFocusLabel]);

  useEffect(() => {
    setValue(defaultValue ?? '');
  }, [defaultValue]);

  // 未編輯時輸入框不吃滑鼠事件，按下落在容器、同時 reactflow(d3-drag) 也會起拖。
  // d3 只要有任何位移就吃掉 click，若靠 click 進編輯，手抖／觸控一下就變成「搬節點、改不了名」。
  // 改在 capture 階段用 pointerdown/pointerup 量位移：≤ EDIT_TAP_SLOP 視為點一下，自行 focus 進編輯。
  const pressPointRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDownCapture = useCallback(
    (e: ReactPointerEvent) => {
      if (disabled || isFocusLabel) return;
      pressPointRef.current = { x: e.clientX, y: e.clientY };
    },
    [disabled, isFocusLabel],
  );

  const handlePointerUpCapture = useCallback(
    (e: ReactPointerEvent) => {
      const press = pressPointRef.current;
      pressPointRef.current = null;
      if (!press || disabled || isFocusLabel) return;
      if (Math.hypot(e.clientX - press.x, e.clientY - press.y) <= EDIT_TAP_SLOP) {
        innerInputRef.current?.focus();
      }
    },
    [disabled, isFocusLabel],
  );

  return (
    <EditorLabelContainerStyled
      onPointerDownCapture={handlePointerDownCapture}
      onPointerUpCapture={handlePointerUpCapture}
      // 擋掉冒泡，避免點名稱被節點當成一般點擊
      onClick={(e: ReactMouseEvent) => e.stopPropagation()}
    >
      <TextFieldStyled
        $editing={isFocusLabel}
        placeholder={placeholder}
        fullWidth={fullWidth}
        className={isFocusLabel ? 'nodrag' : undefined}
        onBlur={handleBlur}
        onFocus={handleFocusLabel}
        inputRef={setInputRef}
        autoComplete="off"
        disabled={disabled}
        inputProps={{
          style: {
            color,
            padding: '4px 8px',
            ...get(globalTheme, `typography.${size}`, {}),
          },
          maxLength: maxLength,
        }}
        onChange={(e) => setValue(e.target.value)}
        value={value}
        error={!!error}
        helperText={helperText}
        onClick={(e) => e.stopPropagation()}
      />
      {isShowTooltips && isFocusLabel && (
        <TextareaTooltip
          limit={maxLength}
          length={value.length}
          sx={{ top: '35px', height: '100%' }}
        />
      )}
    </EditorLabelContainerStyled>
  );
};

export default memo(EditorLabel);
